<template>
  <div class="w-full custom-quill ">
    <header class="flex mb-6">
      <div v-if="!comment.authorUser.photoProfile">
        <img class="object-cover w-12 h-12 mr-6 rounded-full cursor-pointer" src="@/assets/images/user-topic.svg"
          alt="user" />
      </div>
      <div v-else-if="comment.authorUser.photoProfile">
        <img class="object-cover w-12 h-12 mr-6 rounded-full cursor-pointer" :src="comment.authorUser.photoProfile.path"
          alt="user" />
      </div>
      <span class="flex flex-col gap-2">
        <p class="text-base font-bold text-h-neutral-700">
          {{ comment.authorUser.name }}
        </p>
        <p class="text-xs font-semibold text-h-neutral-700">
          {{
            $t("labels.generic", {
              pt: "Publicado",
              en: "Published",
            })
          }}
          {{ timeSinceCreation }}
        </p>
      </span>
    </header>
    <CommentAnswer :v-if="comment.reply !== null" :reply="comment.reply" />
    <p
      class="text-sm font-semibold leading-6 !p-0 !pt-1 text-h-neutral-800 ql-editor"
      v-html="comment.comment"
    ></p>
    <div v-if="comment?.media" class="!w-full flex justify-center bg-black mt-6 " >
      <Image :src="comment.media.path"   alt="comment image" preview imageClass="!w-full !object-cover !h-[600px] !min-h-[150px] !max-h-[450px]"/>
    </div>

    <footer
      class="flex items-center justify-between pb-6 mt-6 text-base font-bold border-b text-h-primary-500"
    >
      <div class="flex gap-3 cursor-pointer">
        <span class="flex items-center justify-center gap-2 p-2"
          :class="comment.userLiked ? 'text-h-secondary-600' : 'btn-simple'" @click="likeComment">
          <v-icon name="ri-thumb-up-line" scale="1" />
          <p v-if="comment.likeAmount == 1">
            {{ comment.likeAmount }}
            {{
              $t("labels.generic", {
                pt: "curtida",
                en: "likes",
              })
            }}
          </p>
          <p v-else>
            {{ comment.likeAmount }}
            {{
              $t("labels.generic", {
                pt: "curtidas",
                en: "like",
              })
            }}
          </p>
        </span>
        <a class="flex items-center justify-center gap-2 p-2 cursor-pointer btn-simple" href="#box-comment"
          @click="replyComment">
          <v-icon name="io-arrow-undo-sharp" scale="1"></v-icon>
          <p>
            {{
              $t("labels.generic", {
                pt: "Responder",
                en: "Reply",
              })
            }}
          </p>
        </a>
      </div>
      <span>
        <div v-if="commentItems().length > 1" class="custom-dropdownforum md:hidden" >
          <Dropdown dropdownIcon="pi pi-ellipsis-v " :options="commentItems()">
            <template #option="SlotProps">
              <button class="flex gap-2 items-center text-base font-bold w-[212px] btn-simple"
                @click="methodHandler(SlotProps.option.method)">
                <v-icon :name="SlotProps.option.icon"></v-icon>
                <p>
                  {{ $t("labels.generic", {
                  pt: SlotProps.option.labelPT,
                  en: SlotProps.option.labelEN
                  }) }}
                </p>
              </button>
            </template>
          </Dropdown>
        </div>
        <div v-else v-for="option in commentItems()" class="custom-dropdownforum md:hidden w-12 flex justify-center">
          <button class=" gap-2 btn-simple" @click="methodHandler(option.method)">
            <v-icon :name="option.icon" scale="0.8"></v-icon>
          </button>
        </div>
        <div class=" hidden md:flex md:gap-4 md:p-2">
          <button v-for="option in commentItems()"
          class="flex items-center justify-center gap-2 cursor-pointer btn-simple"
          @click="methodHandler(option.method)">
          <v-icon :name="option.icon"></v-icon>
              <p>
                {{ $t("labels.generic", {
                  pt: option.labelPT,
                  en: option.labelEN
                }) }}
              </p>
            </button>
        </div>
      </span>
    </footer>
  </div>
</template>
<script lang="ts">
import { Comment } from "@/models/comment.model";
import { defineAsyncComponent, defineComponent, PropType } from "vue";
import moment from "moment";
import { getUserFromStorage } from "@/utils/user-store.util";
import { ForumService } from "../../../forum.service";
import { StatusTopic, Topic } from "@/models/topic.model";
import { take } from "rxjs";
import { GoToRoute } from "@/utils/go-to.util";
import { Roles } from "@/models/user.model";
import { ToastFactory } from "@/utils/toast-factory.util";
import {Messages} from "@/utils/messages.util";
import CommentCard from "./comment-card.service";
import { commentOptions } from "./items-comment-card.utils";

const CommentAnswer = defineAsyncComponent(
  () => import("@/views/CourseStudent/Forum/components/CommentAnswer/comment-answer.vue")
);

export default defineComponent({
  name: "card-component",
  components: {
    CommentAnswer,
  },
  data() {
    return {
      params: {
        topicId: this.$route.params.topicId,
        courseName: this.$route.params.courseName,
        id: this.$route.params.id,
      },
    };
  },
  props: {
    comment: {
      type: Object as PropType<Comment>,
      required: true,
      default: {},
    },
    topic: {
      type: Object as PropType<Topic>,
      required: true,
      default: {},
    },
  },
  methods: {
    isAuthorOfComment(): boolean {
      return getUserFromStorage().id === this.comment.authorUser.id;
    },
    getUserRoleLogged(): Roles {
      return getUserFromStorage()?.role;
    },
    showDeleteConfirmDialog(): void {
      this.$confirm.require({
            group: "deletingComment",
            header: this.$t('labels.generic', { pt: "Excluir comentário?", en: "Delete comment?" }),
            message: this.$t('labels.generic',
              {
                pt: "Ao excluir, o comentário será excluído e não ficará mais disponível para os usuários. Após a confirmação, a ação não poderá ser desfeita. ",
                en: "When you delete, this comment will be deleted and will no longer be available to users. Once confirmed, the action cannot be undone. "
              }),
            rejectLabel: this.$t('labels.generic', { pt: "Cancelar", en: "Cancel" }),
            acceptLabel: this.$t('labels.generic', { pt: "Excluir comentário", en: "Delete comment." }),
            acceptClass: 'btn-delete-block',
            rejectClass: 'btn-outline',
            accept: () => this.deleteComment()
          })
    },
    likeComment(): void {
      this.service.comment.pipe(take(1)).subscribe({
        next: () => {
          this.setLikedByUser();
        },
      });
      this.service.likeComment(this.topic.id, this.comment.id);
    },
    deleteComment(): void {
      this.service.comment.pipe(take(1)).subscribe({
        next: (response) => {
          if (response !== null) {
            this.$emit("deleteComment")
            this.$toast.add(
             ToastFactory.success(Messages.FORUM_COMMENT_DELETED))
          }
            
          }
      });
      this.service.deleteCommentByTopic(this.topic.id, this.comment.id);
    },
    reportComment(): void {
      this.$emit("commentId", this.comment.id)
      this.$emit("isDenounceDialogVisible")
    },
    replyComment(): void {
      this.$emit("replyComment", this.comment);
    },
    setLikedByUser(): void {
      if (this.comment.userLiked) {
        this.comment.likeAmount -= 1;
      } else {
        this.comment.likeAmount += 1;
      }
      this.comment.userLiked = !this.comment.userLiked;
    },
    redirectToEditComment(): void {
      this.go.redirectToPage("editComment", {
        ...this.params,
        commentId: this.comment.id,
      });
    },

    topicStatus(): StatusTopic {
      return this.topic?.status as StatusTopic;
    },

    commentItems(): Array<commentOptions> {
      return this.commentCardService.displayCommentOptions(this.getUserRoleLogged(), this.isAuthorOfComment(), this.topicStatus());
    },

    methodHandler(method: string): void {
      switch(method) {
        case "deleteComment":
          this.showDeleteConfirmDialog();
          break;
        case "editComment":
          this.redirectToEditComment();
          break;
        case "reportComment":
          this.reportComment();
          break;  
      }
    }
  },
  computed: {
    timeSinceCreation(): string {
      //TODO - when location language activated use navigator.language
      moment.locale("pt-br");
      return moment(this.comment.created).add(3, "hour").fromNow();
    },
    service(): ForumService {
      return new ForumService();
    },
    commentCardService(): CommentCard {
      return new CommentCard()
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
});
</script>
